import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Button,
    TextField,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    CircularProgress,
    Chip,
    Alert,
    Autocomplete,
} from "@mui/material";
import { Add, Edit, Delete, Close, LocalShipping, DirectionsBoat, LinkOff } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import containerService from "../../services/containerService";
import sailingService from "../../services/sailingService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";

const emptyForm = { containerNumber: "", containerType: "", sealNumber: "", notes: "" };

const URL_SCHEMA = {
    search: { default: "" },
    page: { default: 0, parse: (v) => parseInt(v, 10) || 0 },
    rowsPerPage: { default: 20, parse: (v) => parseInt(v, 10) || 20 },
};

const CONTAINER_STATUS_CONFIG = {
    available: { label: "Available", color: "default" },
    assigned: { label: "Assigned", color: "primary" },
    completed: { label: "Completed", color: "success" },
};

const AdminContainers = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    useScrollRestoration();
    const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
    const { search, page, rowsPerPage } = urlState;
    // Local typing buffer, debounced into urlState.search below — seeded
    // from the URL so a restored search still shows in the field.
    const [searchInput, setSearchInput] = useState(urlState.search);

    const [containers, setContainers] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // container being edited, or null for "add new"
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [assignTarget, setAssignTarget] = useState(null); // container being assigned D/R(s)
    const [assignRaw, setAssignRaw] = useState("");
    const [assigning, setAssigning] = useState(false);
    const [removingDr, setRemovingDr] = useState(null); // `${containerId}:${drNumber}` in flight

    const [sailingTarget, setSailingTarget] = useState(null); // container being assigned a sailing
    const [sailingOptions, setSailingOptions] = useState([]);
    const [loadingSailings, setLoadingSailings] = useState(false);
    const [selectedSailing, setSelectedSailing] = useState(null);
    const [assigningSailing, setAssigningSailing] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await containerService.getAll({
                search: search || undefined,
                page: page + 1,
                limit: rowsPerPage,
            });
            setContainers(data.data.containers || []);
            setTotal(data.data.total || 0);
        } catch {
            enqueueSnackbar("Failed to load containers", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Debounced search — only fire once typing pauses. Skipped when
    // searchInput already matches the URL's committed value — true right
    // after mount — so restoring a search from the URL doesn't also reset
    // page back to 0.
    useEffect(() => {
        if (searchInput === search) return;
        const timer = setTimeout(() => {
            setUrlState({ search: searchInput, page: 0 });
        }, 600);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, page, rowsPerPage]);

    const openAdd = () => {
        setEditing(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (container) => {
        setEditing(container);
        setForm({
            containerNumber: container.containerNumber,
            containerType: container.containerType || "",
            sealNumber: container.sealNumber || "",
            notes: container.notes || "",
        });
        setDialogOpen(true);
    };

    const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSave = async () => {
        if (!editing && !form.containerNumber.trim()) {
            enqueueSnackbar("Container number is required", { variant: "error" });
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                const { data } = await containerService.update(editing._id, {
                    containerType: form.containerType || undefined,
                    sealNumber: form.sealNumber || undefined,
                    notes: form.notes || undefined,
                });
                enqueueSnackbar(data.message || "Container updated", { variant: "success" });
            } else {
                await containerService.create({
                    containerNumber: form.containerNumber.trim(),
                    containerType: form.containerType || undefined,
                    sealNumber: form.sealNumber || undefined,
                    notes: form.notes || undefined,
                });
                enqueueSnackbar("Container added", { variant: "success" });
            }
            setDialogOpen(false);
            load();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save container", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const parseDockReceiptNumbers = (raw) =>
        [...new Set(raw.split(/[\n,]+/).map((n) => n.trim()).filter(Boolean))];

    const openAssign = (container) => {
        setAssignTarget(container);
        setAssignRaw("");
    };

    const handleAssignDockReceipts = async () => {
        const numbers = parseDockReceiptNumbers(assignRaw);
        if (!numbers.length) {
            enqueueSnackbar("Enter at least one dock receipt number", { variant: "error" });
            return;
        }
        setAssigning(true);
        try {
            const { data } = await containerService.assignDockReceipts(assignTarget._id, numbers);
            enqueueSnackbar(data.message || "Dock receipt(s) assigned", { variant: "success" });
            setAssignTarget(null);
            load();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to assign dock receipt(s)", { variant: "error" });
        } finally {
            setAssigning(false);
        }
    };

    const handleRemoveDockReceipt = async (container, drNumber) => {
        const key = `${container._id}:${drNumber}`;
        setRemovingDr(key);
        try {
            await containerService.unassignDockReceipt(container._id, drNumber);
            enqueueSnackbar(`Dock receipt ${drNumber} removed`, { variant: "success" });
            load();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to remove dock receipt", { variant: "error" });
        } finally {
            setRemovingDr(null);
        }
    };

    const openAssignSailing = (container) => {
        setSailingTarget(container);
        setSelectedSailing(container.sailing || null);
        setLoadingSailings(true);
        sailingService
            .getAll()
            .then(({ data }) => setSailingOptions(data.data?.schedules || []))
            .catch(() => enqueueSnackbar("Failed to load sailings", { variant: "error" }))
            .finally(() => setLoadingSailings(false));
    };

    const handleAssignSailing = async () => {
        setAssigningSailing(true);
        try {
            const { data } = await containerService.assignSailing(sailingTarget._id, selectedSailing?._id || null);
            enqueueSnackbar(data.message || "Sailing updated", { variant: "success" });
            setSailingTarget(null);
            load();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to assign sailing", { variant: "error" });
        } finally {
            setAssigningSailing(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await containerService.remove(deleteTarget._id);
            enqueueSnackbar("Container deleted", { variant: "success" });
            setDeleteTarget(null);
            load();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to delete container", { variant: "error" });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Containers"
                subtitle="The shared registry of shipping containers — picked from (not re-typed) on BOL upload and item edits, so container numbers never get duplicated by a typo"
                action={
                    <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
                        Add Container
                    </Button>
                }
            />

            <Card>
                <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    <TextField
                        size="small"
                        label="Search"
                        placeholder="Container #, seal #, or type…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ minWidth: 280 }}
                    />
                </Box>

                {loading ? (
                    <SkeletonLoader rows={6} />
                ) : containers.length === 0 ? (
                    <EmptyState
                        icon="📦"
                        title="No containers logged yet"
                        description="Containers are added here automatically when confirmed on a BOL upload, or you can add one manually."
                        actionLabel="Add Container"
                        onAction={openAdd}
                    />
                ) : (
                    <>
                        <Box sx={{ overflowX: "auto" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Container #</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Seal #</TableCell>
                                        <TableCell>Sailing</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Dock Receipts</TableCell>
                                        <TableCell>Notes</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {containers.map((c) => (
                                        <TableRow key={c._id} hover>
                                            <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                                                {c.containerNumber}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                {c.containerType || "—"}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                {c.sealNumber || "—"}
                                            </TableCell>
                                            <TableCell>
                                                {c.sailing ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: "monospace",
                                                            color: "primary.main",
                                                            cursor: "pointer",
                                                            "&:hover": { textDecoration: "underline" },
                                                        }}
                                                        onClick={() => navigate(`/admin/sailing/${c.sailing.sailingNumber}`)}
                                                    >
                                                        {c.sailing.sailingNumber}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={CONTAINER_STATUS_CONFIG[c.status]?.label || c.status}
                                                    size="small"
                                                    color={CONTAINER_STATUS_CONFIG[c.status]?.color || "default"}
                                                    variant={c.status === "available" ? "outlined" : "filled"}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 200 }}>
                                                {(c.dockReceiptNumbers || []).length === 0 ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        None assigned
                                                    </Typography>
                                                ) : (
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                        {c.dockReceiptNumbers.map((dr) => (
                                                            <Chip
                                                                key={dr}
                                                                label={dr}
                                                                size="small"
                                                                sx={{ fontFamily: "monospace" }}
                                                                onDelete={() => handleRemoveDockReceipt(c, dr)}
                                                                disabled={removingDr === `${c._id}:${dr}`}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary" noWrap maxWidth={200}>
                                                    {c.notes || "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Assign Sailing">
                                                    <IconButton size="small" onClick={() => openAssignSailing(c)}>
                                                        <DirectionsBoat fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Assign Dock Receipt">
                                                    <IconButton size="small" onClick={() => openAssign(c)}>
                                                        <LocalShipping fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => openEdit(c)}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => setDeleteTarget(c)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setUrlState({ page: p })}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => setUrlState({ rowsPerPage: parseInt(e.target.value, 10), page: 0 })}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ borderTop: "1px solid", borderColor: "divider" }}
                        />
                    </>
                )}
            </Card>

            {/* Add/Edit dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography fontWeight={700}>{editing ? "Edit Container" : "Add Container"}</Typography>
                    <IconButton size="small" onClick={() => setDialogOpen(false)}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}>
                    <TextField
                        label="Container Number *"
                        value={form.containerNumber}
                        onChange={setField("containerNumber")}
                        disabled={!!editing}
                        helperText={editing ? "Container number can't be changed — delete and re-add if it was logged wrong" : "e.g. XHCU-507608-8"}
                        fullWidth
                    />
                    <TextField
                        label="Container Type"
                        value={form.containerType}
                        onChange={setField("containerType")}
                        placeholder="e.g. 40HC"
                        fullWidth
                    />
                    <TextField
                        label="Seal Number"
                        value={form.sealNumber}
                        onChange={setField("sealNumber")}
                        fullWidth
                    />
                    <TextField
                        label="Notes"
                        value={form.notes}
                        onChange={setField("notes")}
                        multiline
                        minRows={2}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={18} color="inherit" /> : editing ? "Save Changes" : "Add Container"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assign D/R dialog */}
            <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography fontWeight={700}>Assign D/R — {assignTarget?.containerNumber}</Typography>
                    <IconButton size="small" onClick={() => setAssignTarget(null)}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        Every item that already carries one of these D/R numbers will be updated with this
                        container's number/type/seal.
                    </Alert>
                    <TextField
                        label="Dock Receipt Number(s)"
                        placeholder={"1832741\n1832742"}
                        value={assignRaw}
                        onChange={(e) => setAssignRaw(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        helperText="One per line (commas also accepted)"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setAssignTarget(null)} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleAssignDockReceipts} variant="contained" disabled={assigning}>
                        {assigning ? <CircularProgress size={18} color="inherit" /> : "Assign"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assign Sailing dialog */}
            <Dialog open={!!sailingTarget} onClose={() => setSailingTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography fontWeight={700}>Assign Sailing — {sailingTarget?.containerNumber}</Typography>
                    <IconButton size="small" onClick={() => setSailingTarget(null)}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Autocomplete
                        options={sailingOptions}
                        loading={loadingSailings}
                        value={selectedSailing}
                        onChange={(_, val) => setSelectedSailing(val)}
                        getOptionLabel={(o) => `${o.sailingNumber} — ${o.vesselName}`}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        renderInput={(params) => (
                            <TextField {...params} label="Sailing" placeholder="Search existing sailings…" />
                        )}
                    />
                    {sailingTarget?.sailing && (
                        <Button
                            size="small"
                            color="error"
                            startIcon={<LinkOff />}
                            sx={{ mt: 1.5 }}
                            onClick={() => setSelectedSailing(null)}
                        >
                            Unassign from {sailingTarget.sailing.sailingNumber}
                        </Button>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setSailingTarget(null)} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleAssignSailing} variant="contained" disabled={assigningSailing}>
                        {assigningSailing ? <CircularProgress size={18} color="inherit" /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirm dialog */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography fontWeight={700}>Delete Container</Typography>
                    <IconButton size="small" onClick={() => setDeleteTarget(null)}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2">
                        Delete container <strong>{deleteTarget?.containerNumber}</strong> from the registry? Items
                        that already reference it are unaffected — this only removes it from the picker.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDeleteTarget(null)} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}>
                        {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminContainers;
